import {
  Button,
  Divider,
  Grid,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles, WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { reduxForm, Field } from 'redux-form';
import { compose } from 'recompose';
import {
  renderSearchFieldWithAttachedField,
  renderMultipleDropdown,
  renderTextField,
} from 'components/form/field/redux-field';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import HTTP from 'services/http';
import { searchFinancialFundations } from 'actions/action-financials';
import { FinancialAssistanceFundsStyles } from './styles';
import { FinancialAssistanceFundsTable, IResponse } from './table';

function FinancialAssistanceFunds(props: any) {
  const { classes } = props;
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [data, setData] = useState<IResponse>({ results: [], totalCount: 0 } as IResponse);
  const [enableSearch, setEnableSearch] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [therapeuticAreaOptions, setTherapeuticAreaOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const dispatch = useDispatch();
  const financialAssistanceFunds = useSelector(
    (state: any) => state.financials.financial_assistance_funds,
  );
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    program_type: [],
    coverage_eligibility: [],
    funding_status: [],
    program_brand: [],
    therapeutic_area: [],
    manufacturer: '',
    medication: '',
    program_name: '',
  });
  const debouncedSetSearchParams = useMemo(
    () =>
      debounce((field: string, value: any) => {
        setSearchParams(prev => ({
          ...prev,
          [field]: value,
        }));
      }, 300),
    [],
  );

  const handleSearchParamChange = (field: string) => (value: any) => {
    debouncedSetSearchParams(field, value.target.value);
  };

  const handleSelectParamChange = (field: string) => (value: any) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value === '' ? [] : value,
    }));
  };

  useEffect(() => {
    setData({ results: [], totalCount: 0 } as IResponse);
    if (financialAssistanceFunds.results.length > 0) {
      const transformedResults = financialAssistanceFunds.results.map((item: any) => ({
        id: item.ProgramID,
        program_name: item.ProgramName,
        medication: item.Drugs.join(', '),
        manufacturer: item.Manufacturers.join(', '),
        funding_status: item.FoundationFundLevels.join(', '),
        is_waitlist:
          Boolean(item.isWaitList) || item.ProgramName.toLowerCase().includes('waitlist'),
        category: '',
        type: item.AssistanceType,
        therapeutic_area: item.TherapAreas.join(', '),
        eligibility: item.Eligibilities.join(', '),
        children: [],
      }));
      setData({ results: transformedResults, totalCount: transformedResults.length } as IResponse);
    }
  }, [financialAssistanceFunds]);

  useEffect(() => {
    if (
      searchParams.program_name.trim() === '' &&
      searchParams.program_type.length === 0 &&
      searchParams.coverage_eligibility.length === 0 &&
      searchParams.funding_status.length === 0 &&
      searchParams.therapeutic_area.length === 0 &&
      searchParams.manufacturer === '' &&
      searchParams.medication === ''
    ) {
      setEnableSearch(false);
    } else {
      setEnableSearch(true);
    }
  }, [
    searchParams.program_name,
    searchParams.program_type,
    searchParams.coverage_eligibility,
    searchParams.funding_status,
    searchParams.therapeutic_area,
    searchParams.manufacturer,
    searchParams.medication,
  ]);

  const handleSearch = () => {
    dispatch(searchFinancialFundations(searchParams));
    setLoading(true);
  };

  useEffect(() => {
    setLoading(false);
  }, [financialAssistanceFunds]);

  useEffect(() => {
    HTTP.get('/mmit/therapeutic-areas').then((response: any) => {
      const options = (response.data as string[]).map((area: string) => ({
        value: area,
        label: area,
      }));
      setTherapeuticAreaOptions(options);
    });
  }, []);

  const programTypeOptions = [
    { value: 'Copay', label: 'Manufacturer Copay Card' },
    { value: 'PAP', label: 'PAP' },
    { value: 'Free Drug', label: 'Manufacturer Prescription Assistance Program (Free Drug)' },
    { value: 'Foundation', label: 'Foundation' },
  ];
  const coverageEligibilityOptions = [
    { value: 'Commercially insured', label: 'Commercial' },
    { value: 'Underinsured', label: 'Underinsured' },
    { value: 'Uninsured', label: 'Uninsured' },
    { value: 'Medicare', label: 'Medicare' },
  ];

  const fundingStatusOptions = [
    { value: 'Empty', label: 'Empty' },
    { value: 'Low', label: 'Low' },
    { value: 'Normal', label: 'Normal' },
  ];

  return (
    <div className={classes.container}>
      <Grid container>
        <Grid item xs={12}>
          <Typography className={classes.title}>Financial Assistance Funds</Typography>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={6} spacing={2} gap={2}>
          <Field
            name="medication"
            label="Medication"
            component={renderTextField}
            onChange={handleSearchParamChange('medication')}
            placeholder="Medication"
            disabled={loading}
          />
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={!enableSearch || loading}
            color="primary"
          >
            Search
          </Button>
        </Grid>
        <Grid container className={classes.rowSeparator} spacing={3} gap={3}>
          <Grid item xs={4}>
            <Field
              name="program_type"
              label="Program Type"
              component={renderMultipleDropdown}
              onChange={handleSelectParamChange('program_type')}
              fields={programTypeOptions}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={4}>
            <Field
              name="coverage_eligibility"
              label="Coverage Eligibility"
              component={renderMultipleDropdown}
              onChange={handleSelectParamChange('coverage_eligibility')}
              fields={coverageEligibilityOptions}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={4}>
            <Field
              name="funding_status"
              label="Fund status"
              component={renderMultipleDropdown}
              onChange={handleSelectParamChange('funding_status')}
              fields={fundingStatusOptions}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={4}>
            <Field
              name="therapeutic_area"
              label="Therapeutic Area"
              component={renderMultipleDropdown}
              onChange={handleSelectParamChange('therapeutic_area')}
              fields={therapeuticAreaOptions}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container className={classes.rowSeparator}>
        <Grid item xs={12}>
          {loading && <CircularProgress />}
          <FinancialAssistanceFundsTable searchTerm="" data={data} loading={loading} />
        </Grid>
      </Grid>
    </div>
  );
}
export default compose(
  withStyles(FinancialAssistanceFundsStyles),
  reduxForm({ form: 'financialAssistanceFunds' }),
)(FinancialAssistanceFunds);
